import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { useTranslation } from '@/hooks/useTranslation';
import { buildDisplayName, getFirstName } from '@/lib/user-utils';
import { logger } from '@/services/loggingService';

interface UserRole {
  id: string;
  role: string;
  context?: string;
  permissions?: Record<string, any>;
}

interface CompanyData {
  id: string;
  name: string;
  logo_url?: string;
  company_identifier?: string;
  short_code?: string;
  member_role: string; // Legacy field for backward compatibility
  isOwner?: boolean; // New: Is user the original owner or designated owner
  isOriginalOwner?: boolean; // New: Is user the original company creator
  userPermissions?: string[]; // New: User's specific permissions for this company
}

interface SimplifiedAuthContextType {
  // Core auth state
  user: User | null;
  session: Session | null;
  
  // Loading states
  isAuthInitializing: boolean;
  isDataLoading: boolean;
  isAuthenticated: boolean;
  
  // Data state (single source of truth)
  roles: UserRole[];
  companies: CompanyData[];
  selectedCompanyId: string | null;
  selectedCompany: CompanyData | null;
  
  // Actions
  setSelectedCompanyId: (id: string | null) => void;
  refetchData: () => void;
  
  // Access flags (computed)
  hasInternalAccess: boolean;
  hasCompanyAccess: boolean;
  hasRecruiterAccess: boolean;
  hasTalentAccess: boolean;
  hasNannyAccess: boolean;
  hasMotherAccess: boolean;
  
  // Owner flags (enhanced)
  isCompanyOwner: boolean;
  isOriginalCompanyOwner: boolean;
  isInternalOwner: boolean;
  isOriginalInternalOwner: boolean;
  
  // Company permission flags 
  canManageCompany: boolean;
  canManageTeam: boolean;
  canManageJobs: boolean;
  canViewApplications: boolean;
  canManageApplications: boolean;
  canInviteMembers: boolean;
  
  // Internal permission flags (new)
  canManageInternal: boolean;
  canManageInternalUsers: boolean;
  canManageAllCompanies: boolean;
  canViewAllApplications: boolean;
  canManageSystemSettings: boolean;
  
  // Internal permissions data
  internalPermissions: string[];
  
  // Convenience flags
  isReady: boolean;
  isFullyLoaded: boolean;
}

const SimplifiedAuthContext = createContext<SimplifiedAuthContextType | null>(null);

// Helper functions for data fetching - MUST be defined before component uses them
export const fetchUserRoles = async (userId: string): Promise<UserRole[]> => {
  try {
    const { data: roles, error } = await supabase
      .rpc('get_user_roles', { user_uuid: userId });

    if (error) {
      logger.error('Roles query failed', error, {
        component: 'SimplifiedAuthContext',
        metadata: { userId }
      });
      return [{ id: 'talent', role: 'talent' }];
    }

    if (!roles || (roles as any[]).length === 0) {
      return [{ id: 'talent', role: 'talent' }];
    }

    return (roles as any[]).map((role: any) => ({
      id: role.id,
      role: role.role,
      permissions: role.permissions || {}
    }));
  } catch (error) {
    logger.error('fetchUserRoles failed', error as Error, {
      component: 'SimplifiedAuthContext',
      metadata: { userId }
    });
    return [{ id: 'talent', role: 'talent' }];
  }
};

export const fetchUserCompanies = async (userId: string): Promise<CompanyData[]> => {
  const { data: memberships, error: membershipsError } = await supabase
    .from('company_team_members')
    .select(`company_id, member_role, company_profiles ( id, name, logo_url, company_identifier, short_code )`)
    .eq('user_id', userId as any);

  if (membershipsError) {
    logger.error('Companies query failed', membershipsError, {
      component: 'SimplifiedAuthContext',
      metadata: { userId }
    });
    return [];
  }

  if (!memberships || memberships.length === 0) {
    return [];
  }

  return await Promise.all(memberships.map(async (m: any) => {
    const { data: ownershipData } = await supabase
      .from('company_profiles')
      .select('user_id')
      .eq('id', m.company_id)
      .single();
    
    const isOriginalOwner = (ownershipData as any)?.user_id === userId;
    const hasOwnerRole = m.member_role === 'owner';
    
    const { data: permissions } = await supabase
      .rpc('get_user_company_permissions', {
        p_user_id: userId,
        p_company_id: m.company_id
      });
    
    const userPermissions = ((permissions as any[]) || []).map((p: any) => p.permission_key);
    
    return {
      id: m.company_id,
      name: m.company_profiles?.name || 'Company',
      logo_url: m.company_profiles?.logo_url || undefined,
      company_identifier: m.company_profiles?.company_identifier || undefined,
      short_code: m.company_profiles?.short_code || undefined,
      member_role: m.member_role || 'member',
      isOwner: isOriginalOwner || hasOwnerRole,
      isOriginalOwner,
      userPermissions
    };
  }));
};

export const useSimplifiedAuth = () => {
  const context = useContext(SimplifiedAuthContext);
  if (!context) {
    throw new Error('useSimplifiedAuth must be used within a SimplifiedAuthProvider');
  }
  return context;
};

export const SimplifiedAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const toast = useTranslatedToast();
  const { t } = useTranslation('toast');
  
  // Core auth state
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);
  const [hasShownLoginSuccess, setHasShownLoginSuccess] = useState(false);
  const [lastWelcomeUserId, setLastWelcomeUserId] = useState<string | null>(null);
  
  
  // Track last logout time to prevent cache reuse after logout
  const [lastLogoutTime, setLastLogoutTime] = useState<number>(() => {
    try {
      return parseInt(localStorage.getItem('last_logout_time') || '0');
    } catch {
      return 0;
    }
  });

  // Track if we have valid cached data to determine if we should show loading spinner
  const [hasCachedData, setHasCachedData] = useState(() => {
    try {
      const cachedRoles = localStorage.getItem('auth_roles_cache');
      const cachedCompanies = localStorage.getItem('auth_companies_cache');
      if (cachedRoles && cachedCompanies) {
        const { timestamp: rolesTime } = JSON.parse(cachedRoles);
        const { timestamp: companiesTime } = JSON.parse(cachedCompanies);
        const lastLogout = parseInt(localStorage.getItem('last_logout_time') || '0');
        // Valid if both caches exist, are recent, and after last logout
        return rolesTime > lastLogout && companiesTime > lastLogout &&
               Date.now() - rolesTime < 5 * 60 * 1000;
      }
    } catch {}
    return false;
  });

  // Helper function to check if welcome was already shown recently (within 30 minutes)
  const wasWelcomeShownRecently = (userId: string): boolean => {
    try {
      const sessionKey = `welcome_shown_${userId}`;
      const timestampStr = sessionStorage.getItem(sessionKey);
      if (!timestampStr) return false;

      const timestamp = parseInt(timestampStr, 10);
      const now = Date.now();
      const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds

      return (now - timestamp) < thirtyMinutes;
    } catch {
      return false;
    }
  };

  // Helper function to mark welcome as shown with current timestamp
  const markWelcomeShown = (userId: string): void => {
    try {
      const sessionKey = `welcome_shown_${userId}`;
      sessionStorage.setItem(sessionKey, Date.now().toString());
    } catch {
      // Ignore sessionStorage errors
    }
  };
  
  // Data state with localStorage caching
  const [roles, setRoles] = useState<UserRole[]>(() => {
    try {
      const cached = localStorage.getItem('auth_roles_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const lastLogout = parseInt(localStorage.getItem('last_logout_time') || '0');
        
        // CRITICAL: Reject cache if it's older than last logout
        if (timestamp < lastLogout) {
          localStorage.removeItem('auth_roles_cache');
          return [];
        }
        
        // Use cache if less than 5 minutes old AND after last logout
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch {}
    return [];
  });
  
  const [companies, setCompanies] = useState<CompanyData[]>(() => {
    try {
      const cached = localStorage.getItem('auth_companies_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const lastLogout = parseInt(localStorage.getItem('last_logout_time') || '0');
        
        // CRITICAL: Reject cache if it's older than last logout
        if (timestamp < lastLogout) {
          localStorage.removeItem('auth_companies_cache');
          return [];
        }
        
        // Use cache if less than 5 minutes old AND after last logout
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch {}
    return [];
  });
  
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('selectedCompanyId') || null;
    } catch {
      return null;
    }
  });

  // Cache roles and companies
  const cacheRoles = (roles: UserRole[]) => {
    try {
      localStorage.setItem('auth_roles_cache', JSON.stringify({
        data: roles,
        timestamp: Date.now()
      }));
    } catch {}
  };

  const cacheCompanies = (companies: CompanyData[]) => {
    try {
      localStorage.setItem('auth_companies_cache', JSON.stringify({
        data: companies,
        timestamp: Date.now()
      }));
    } catch {}
  };

  // Request deduplication - use ref to persist across renders
  const activeDataFetchRef = useRef<Promise<void> | null>(null);
  
  // Debounce tracking for SIGNED_IN events
  const lastSignInEventRef = useRef<number>(0);
  
  // Track current user ID to distinguish fresh login vs tab revalidation
  const currentUserIdRef = useRef<string | null>(null);
  
  // Track hasCachedData in ref to avoid stale closure issues
  const hasCachedDataRef = useRef(hasCachedData);
  useEffect(() => {
    hasCachedDataRef.current = hasCachedData;
  }, [hasCachedData]);

  // Streamlined data fetching - preserve critical logic
  const fetchUserData = async (userId: string, isBackgroundRefresh = false) => {
    if (activeDataFetchRef.current) return activeDataFetchRef.current; // Prevent concurrent requests
    
    // SAFETY: Verify this is still the current user
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user || session.user.id !== userId) {
      console.warn('[Auth] Fetch cancelled - user mismatch');
      return;
    }
    
    // Only show loading if NO cache available AND not a background refresh
    // Use ref to get current value and avoid stale closure
    if (!hasCachedDataRef.current && !isBackgroundRefresh) {
      console.log('[Auth] Setting isDataLoading=true (no cache, not background)');
      setIsDataLoading(true);
    }
    
    activeDataFetchRef.current = (async () => {
      try {
        // Fetch roles and companies in parallel (PRESERVE THIS CRITICAL LOGIC)
        const [rolesResult, companiesResult] = await Promise.allSettled([
          fetchUserRoles(userId),
          fetchUserCompanies(userId)
        ]);
        
        // Handle roles (PRESERVE THIS CRITICAL LOGIC)
        if (rolesResult.status === 'fulfilled') {
          setRoles(rolesResult.value);
          cacheRoles(rolesResult.value);
        } else {
          setRoles([]);
        }
        
        // Handle companies (PRESERVE THIS CRITICAL LOGIC)
        if (companiesResult.status === 'fulfilled') {
          setCompanies(companiesResult.value);
          cacheCompanies(companiesResult.value);
          
          // Enhanced auto-select company logic with validation
          const currentCompanyId = selectedCompanyId || localStorage.getItem('selectedCompanyId');
          const isValidCompany = currentCompanyId && 
            companiesResult.value.some(c => c.id === currentCompanyId);
          
          if (!isValidCompany && companiesResult.value.length > 0) {
            // Invalid or no company selected - auto-select first company
            const newCompanyId = companiesResult.value[0].id;
            setSelectedCompanyId(newCompanyId);
            localStorage.setItem('selectedCompanyId', newCompanyId);
            console.log('[Auth] Auto-selected company:', newCompanyId);
          } else if (isValidCompany && currentCompanyId !== selectedCompanyId) {
            // Sync state with localStorage if valid
            setSelectedCompanyId(currentCompanyId);
          }
        } else {
          setCompanies([]);
        }
      } catch (error) {
        console.error('Auth data fetch failed:', error);
      } finally {
        setIsDataLoading(false);
        setHasCachedData(true); // Mark that we now have cached data
        activeDataFetchRef.current = null;
      }
    })();
    
    return activeDataFetchRef.current;
  };


  // Streamlined auth state management
  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const user = session?.user ?? null;

      // Debug logging for auth state changes
      console.log('[Auth] State change:', { event, userId: user?.id, timestamp: new Date().toISOString() });

      setSession(session);
      setUser(user);
      setIsAuthInitializing(false);
      
      // Handle login success notification
      if (event === 'SIGNED_IN' && user && session && !wasWelcomeShownRecently(user.id)) {
        setHasShownLoginSuccess(true);
        setLastWelcomeUserId(user.id);
        markWelcomeShown(user.id);
        
        // Fetch user profile for proper name
        setTimeout(async () => {
          try {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('first_name, last_name, email')
              .eq('user_id', user.id as any)
              .single();

            // If profile names are missing, force name collection flow
            try {
              if (!(profile as any)?.first_name || !(profile as any)?.last_name) {
                localStorage.setItem('force_name_collection', 'true');
              }
            } catch {}
            
            const name = profile ? getFirstName(profile as any) : getFirstName({ email: user.email });
            
            toast.raw.success(t('auth.welcome_back_personalized', { name }), {
              description: t('auth.welcome_description'),
            });
          } catch (error) {
            // Fallback to user metadata/email if profile fetch fails
            const firstName = user.user_metadata?.first_name;
            const emailPrefix = user.email?.split('@')[0];
            let name = firstName || emailPrefix || 'there';
            name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
            
            toast.raw.success(t('auth.welcome_back_personalized', { name }), {
              description: t('auth.welcome_description'),
            });
          }
        }, 0);
      }
      
      // Reset data on sign out with comprehensive cleanup
      if (event === 'SIGNED_OUT') {
        // Clear React state immediately
        setRoles([]);
        setCompanies([]);
        setSelectedCompanyId(null);
        setIsDataLoading(false);
        setHasShownLoginSuccess(false);
        setLastWelcomeUserId(null);
        setHasCachedData(false); // Clear cache flag on logout
        currentUserIdRef.current = null; // Reset user tracking for next fresh login
        
        // Update logout timestamp
        const logoutTime = Date.now();
        setLastLogoutTime(logoutTime);
        try {
          localStorage.setItem('last_logout_time', logoutTime.toString());
        } catch {}
        
        // CRITICAL: Clear storage immediately (async import wrapped properly)
        (async () => {
          try {
            const { clearAuthStorage } = await import('@/utils/constants');
            clearAuthStorage();
          } catch (error) {
            console.error('Failed to clear auth storage:', error);
            // Fallback - try manual cleanup
            try {
              localStorage.clear(); // Nuclear option - clear everything
              sessionStorage.clear();
            } catch {}
          }
        })();
      }
      
      // Fetch data on sign-in with debounce to prevent duplicate events
      if (event === 'SIGNED_IN' && user) {
        const now = Date.now();
        // Debounce: skip if SIGNED_IN fired within last 500ms
        if (now - lastSignInEventRef.current < 500) {
          console.log('[Auth] Skipping duplicate SIGNED_IN event');
          return;
        }
        lastSignInEventRef.current = now;
        
        // Check if this is same user (tab revalidation) vs new user (fresh login)
        const isSameUser = currentUserIdRef.current === user.id;
        currentUserIdRef.current = user.id;
        
        if (isSameUser) {
          // Tab revalidation: same user, do background refresh without clearing cache
          console.log('[Auth] Same user session revalidation, background refresh only');
          setTimeout(() => fetchUserData(user.id, true), 0);
          return;
        }
        
        // Fresh login: clear cache and fetch with loading state
        console.log('[Auth] New user login, clearing cache');
        try {
          localStorage.removeItem('auth_roles_cache');
          localStorage.removeItem('auth_companies_cache');
          setHasCachedData(false);
        } catch {}
        
        console.log('[Auth] Fetching user data on sign-in');
        // Pass false directly since we just cleared the cache (avoid stale closure)
        setTimeout(() => fetchUserData(user.id, false), 0);
      }
    });

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsAuthInitializing(false);
      
      // Fetch data for existing session
      if (session?.user) {
        setTimeout(() => fetchUserData(session.user.id), 0);
      }
    }).catch(() => {
      setIsAuthInitializing(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Set up real-time listeners for permission changes - deferred to reduce initial load
  useEffect(() => {
    if (!user?.id) return;

    const channels: ReturnType<typeof supabase.channel>[] = [];

    // Listen to user_roles changes immediately (critical)
    const userRolesChannel = supabase
      .channel('user_roles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          console.log('User roles changed:', payload);
          try {
            localStorage.removeItem('auth_roles_cache');
            localStorage.removeItem('auth_companies_cache');
          } catch {}
          await fetchUserData(user.id);
        }
      )
      .subscribe();

    channels.push(userRolesChannel);

    // Defer non-critical subscriptions by 2 seconds to reduce initial load pressure
    const deferredTimer = setTimeout(() => {
      // Listen to company_team_members changes
      const teamMembersChannel = supabase
        .channel('team_members_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'company_team_members',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            console.log('Team membership changed:', payload);
            try {
              localStorage.removeItem('auth_companies_cache');
            } catch {}
            await fetchUserData(user.id);
          }
        )
        .subscribe();

      // Listen to internal_portal_users changes
      const internalUsersChannel = supabase
        .channel('internal_users_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'internal_portal_users',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            console.log('Internal portal access changed:', payload);
            try {
              localStorage.removeItem('auth_roles_cache');
            } catch {}
            await fetchUserData(user.id);
          }
        )
        .subscribe();

      // Listen to permission changes
      const permissionsChannel = supabase
        .channel('permissions_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'company_member_permissions',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            console.log('Company permissions changed:', payload);
            try {
              localStorage.removeItem('auth_companies_cache');
            } catch {}
            await fetchUserData(user.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'internal_member_permissions',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            console.log('Internal permissions changed:', payload);
            await fetchUserData(user.id);
          }
        )
        .subscribe();

      channels.push(teamMembersChannel, internalUsersChannel, permissionsChannel);
    }, 2000); // Defer by 2 seconds

    return () => {
      clearTimeout(deferredTimer);
      channels.forEach(channel => {
        supabase.removeChannel(channel);
      });
    };
  }, [user?.id]);

  // Persist selected company
  useEffect(() => {
    if (selectedCompanyId) {
      try {
        localStorage.setItem('selectedCompanyId', selectedCompanyId);
      } catch (e) {
        console.warn('Failed to persist selectedCompanyId:', e);
      }
    }
  }, [selectedCompanyId]);

  // Refetch user data
  const refetchData = () => {
    if (user) {
      fetchUserData(user.id);
    }
  };

  // Computed values
  const isAuthenticated = !!user;
  
  const selectedCompany = useMemo(() => {
    return selectedCompanyId ? companies.find(c => c.id === selectedCompanyId) || null : null;
  }, [selectedCompanyId, companies]);

  // Access flags - computed from roles
  const hasInternalAccess = useMemo(() => {
    return roles.some(role => ['internal_owner', 'internal_member'].includes(role.role));
  }, [roles]);

  const hasCompanyAccess = useMemo(() => companies.length > 0, [companies]);

  const hasRecruiterAccess = useMemo(() => {
    return roles.some(role => role.role === 'recruiter');
  }, [roles]);

  const hasTalentAccess = useMemo(() => {
    return roles.some(role => role.role === 'talent');
  }, [roles]);

  const hasNannyAccess = useMemo(() => {
    return roles.some(role => role.role === 'nanny');
  }, [roles]);

  const hasMotherAccess = useMemo(() => {
    return roles.some(role => role.role === 'mother');
  }, [roles]);

  // Enhanced owner flags - computed from roles and user data
  const isInternalOwner = useMemo(() => {
    return roles.some(role => role.role === 'internal_owner');
  }, [roles]);

  const isOriginalInternalOwner = useMemo(() => {
    // For now, we assume the first internal_owner is the original founder
    // This could be enhanced later with additional database fields
    return isInternalOwner;
  }, [isInternalOwner]);

  // Internal permissions - computed from roles
  const internalPermissions = useMemo(() => {
    if (!hasInternalAccess || !user) return [];
    
    // This will be populated by a function call in the future
    // For now, return empty array to avoid errors
    return [];
  }, [hasInternalAccess, user]);

  // Owner flags - computed from selected company
  const isCompanyOwner = useMemo(() => {
    return selectedCompany?.isOwner || false;
  }, [selectedCompany]);

  const isOriginalCompanyOwner = useMemo(() => {
    return selectedCompany?.isOriginalOwner || false;
  }, [selectedCompany]);

  // Company permission flags - computed from selected company permissions with owner bypass
  const canManageCompany = useMemo(() => {
    if (!selectedCompany?.userPermissions) return false;
    return selectedCompany.isOwner || selectedCompany.userPermissions.includes('manage_company_profile');
  }, [selectedCompany]);

  const canManageTeam = useMemo(() => {
    if (!selectedCompany?.userPermissions) return false;
    return selectedCompany.isOwner || selectedCompany.userPermissions.includes('manage_member_permissions');
  }, [selectedCompany]);

  const canManageJobs = useMemo(() => {
    if (!selectedCompany?.userPermissions) return false;
    const jobPermissions = ['create_jobs', 'edit_jobs', 'delete_jobs', 'manage_job_status'];
    return selectedCompany.isOwner || jobPermissions.some(perm => 
      selectedCompany.userPermissions?.includes(perm)
    );
  }, [selectedCompany]);

  const canViewApplications = useMemo(() => {
    if (!selectedCompany?.userPermissions) return false;
    return selectedCompany.isOwner || selectedCompany.userPermissions.includes('view_applications');
  }, [selectedCompany]);

  const canManageApplications = useMemo(() => {
    if (!selectedCompany?.userPermissions) return false;
    return selectedCompany.isOwner || selectedCompany.userPermissions.includes('manage_applications');
  }, [selectedCompany]);

  const canInviteMembers = useMemo(() => {
    if (!selectedCompany?.userPermissions) return false;
    return selectedCompany.isOwner || selectedCompany.userPermissions.includes('invite_members');
  }, [selectedCompany]);

  // Internal permission flags - computed from internal permissions with owner bypass
  const canManageInternal = useMemo(() => {
    return isInternalOwner || internalPermissions.includes('internal_owner');
  }, [isInternalOwner, internalPermissions]);

  const canManageInternalUsers = useMemo(() => {
    return isInternalOwner || internalPermissions.includes('manage_internal_users');
  }, [isInternalOwner, internalPermissions]);

  const canManageAllCompanies = useMemo(() => {
    return isInternalOwner || internalPermissions.includes('manage_all_companies');
  }, [isInternalOwner, internalPermissions]);

  const canViewAllApplications = useMemo(() => {
    return isInternalOwner || internalPermissions.includes('view_all_applications');
  }, [isInternalOwner, internalPermissions]);

  const canManageSystemSettings = useMemo(() => {
    return isInternalOwner || internalPermissions.includes('manage_system_settings');
  }, [isInternalOwner, internalPermissions]);

  // Convenience flags
  const isReady = useMemo(() => !isAuthInitializing, [isAuthInitializing]);
  const isFullyLoaded = useMemo(() => !isAuthInitializing && !isDataLoading, [isAuthInitializing, isDataLoading]);

  const contextValue = useMemo(() => ({
    user,
    session,
    isAuthInitializing,
    isDataLoading,
    isAuthenticated,
    roles,
    companies,
    selectedCompanyId,
    selectedCompany,
    setSelectedCompanyId,
    refetchData,
    hasInternalAccess,
    hasCompanyAccess,
    hasRecruiterAccess,
    hasTalentAccess,
    hasNannyAccess,
    hasMotherAccess,
    isCompanyOwner,
    isOriginalCompanyOwner,
    isInternalOwner,
    isOriginalInternalOwner,
    canManageCompany,
    canManageTeam,
    canManageJobs,
    canViewApplications,
    canManageApplications,
    canInviteMembers,
    canManageInternal,
    canManageInternalUsers,
    canManageAllCompanies,
    canViewAllApplications,
    canManageSystemSettings,
    internalPermissions,
    isReady,
    isFullyLoaded,
  }), [
    user,
    session,
    isAuthInitializing,
    isDataLoading,
    isAuthenticated,
    roles,
    companies,
    selectedCompanyId,
    selectedCompany,
    hasInternalAccess,
    hasCompanyAccess,
    hasRecruiterAccess,
    hasTalentAccess,
    hasNannyAccess,
    hasMotherAccess,
    isCompanyOwner,
    isOriginalCompanyOwner,
    isInternalOwner,
    isOriginalInternalOwner,
    canManageCompany,
    canManageTeam,
    canManageJobs,
    canViewApplications,
    canManageApplications,
    canInviteMembers,
    canManageInternal,
    canManageInternalUsers,
    canManageAllCompanies,
    canViewAllApplications,
    canManageSystemSettings,
    internalPermissions,
    isReady,
    isFullyLoaded,
  ]);

  return (
    <SimplifiedAuthContext.Provider value={contextValue}>
      {children}
    </SimplifiedAuthContext.Provider>
  );
};

// Export the useSimplifiedAuth hook
